import { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useGetMtoRunFreshnessQuery, useReformMtoRunMutation } from '@shared/api';
import { apiErrorMessage, runRaw, vedomostChangeLabel } from '@entities/mto';
import type { MtoRun } from '@entities/mto';
import { Button } from '@shared/ui/Button';
import { IconRefresh } from '@shared/ui/icons';

/** Повторная проверка не чаще раза в две минуты: она пересобирает ведомости
 *  документов по текущему разбору ПТО. */
const RECHECK_SEC = 120;

/**
 * Актуальность ведомости по РД. Ведомость — снимок разбора ПТО на момент
 * формирования; потом ПТО может его дополнить (объёмы из текста листов
 * извлекаются уже после того, как документ стал «разобран»), переразобрать
 * документ или выпустить новую редакцию. Здесь — что изменилось и кнопка
 * «Сформировать заново».
 */
export function VedomostFreshness({ run, canWrite }: { run: MtoRun; canWrite: boolean }) {
  const navigate = useNavigate();
  const raw = runRaw(run);
  const { currentData: fresh } = useGetMtoRunFreshnessQuery(run.id, {
    skip: run.status !== 'completed',
    refetchOnMountOrArgChange: RECHECK_SEC,
  });
  const [reform, { isLoading: reforming }] = useReformMtoRunMutation();
  const [error, setError] = useState<string | null>(null);

  const handleReform = async () => {
    setError(null);
    try {
      const res = await reform(run.id).unwrap();
      navigate(`/agents/mto/runs/${res.run_id}`);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось сформировать ведомость заново.');
    }
  };

  const carried = raw.carried_decisions ?? 0;
  const origin = raw.reformed_from ? (
    <Info>
      Сформирована заново из{' '}
      <Link to={`/agents/mto/runs/${raw.reformed_from}`}>прежней ведомости</Link>
      {carried > 0 ? `; перенесено решений по строкам: ${carried}.` : '.'}
    </Info>
  ) : null;

  if (fresh?.reformed_to) {
    return (
      <>
        {origin}
        <Info role="status">
          Эта ведомость уже сформирована заново —{' '}
          <Link to={`/agents/mto/runs/${fresh.reformed_to}`}>открыть новую</Link>.
        </Info>
      </>
    );
  }

  if (!fresh?.stale) return origin;

  return (
    <>
      {origin}
      <Stale role="status">
        <StaleTitle>
          Ведомость устарела: после формирования изменились документы в библиотеке РД
        </StaleTitle>
        <ul>
          {fresh.changes.map((c) => (
            <li key={`${c.revision_id}-${c.change}`}>
              <strong>{c.label}</strong> — {vedomostChangeLabel(c.change)}
            </li>
          ))}
        </ul>
        {fresh.can_reform ? (
          canWrite ? (
            <Actions>
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                onClick={handleReform}
                disabled={reforming}
              >
                <IconRefresh size={14} />
                {reforming ? 'Запуск…' : 'Сформировать заново'}
              </Button>
              <Hint>
                Будет новая ведомость по текущему разбору ПТО. Решения «Верно» и «Убрать»
                перенесутся на строки, которые не изменились; эта ведомость останется как была.
              </Hint>
            </Actions>
          ) : (
            <Hint>Сформировать ведомость заново может специалист с правом записи.</Hint>
          )
        ) : (
          <Hint>
            Ведомость собрана из своих файлов, а они после обработки не хранятся. Загрузите файлы
            снова: найденные в библиотеке РД возьмутся из обновлённого разбора.
          </Hint>
        )}
        {error && <ErrorLine role="alert">{error}</ErrorLine>}
      </Stale>
    </>
  );
}

/**
 * Сверка сделана по ведомости, которая с тех пор устарела, — сверку тоже стоит
 * повторить по новой ведомости.
 */
export function SourceVedomostFreshness({ rdRunId }: { rdRunId: string }) {
  const { currentData: fresh } = useGetMtoRunFreshnessQuery(rdRunId, {
    refetchOnMountOrArgChange: RECHECK_SEC,
  });
  if (!fresh?.stale && !fresh?.reformed_to) return null;
  const target = fresh.reformed_to ?? rdRunId;
  return (
    <Stale role="status">
      Ведомость, по которой сделана сверка, устарела: после её формирования изменились документы в
      библиотеке РД.{' '}
      {fresh.reformed_to ? (
        <>
          Ведомость уже сформирована заново —{' '}
          <Link to={`/agents/mto/runs/${target}`}>откройте новую</Link> и сверьте заявки с ней.
        </>
      ) : (
        <>
          <Link to={`/agents/mto/runs/${target}`}>Сформируйте ведомость заново</Link> и повторите
          сверку.
        </>
      )}
    </Stale>
  );
}

const Stale = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-left-width: 4px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  line-height: 1.5;

  & ul {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
    padding-left: ${({ theme }) => theme.spacing(5)};
  }
`;

const StaleTitle = styled.strong`
  display: block;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Hint = styled.span`
  display: block;
  flex: 1 1 260px;
  margin-top: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Info = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorLine = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.status.danger};
`;
